<template>
    <div class="invest-page">
        <main class="page-shell">
            <!-- 顶部栏 -->
            <header class="top-nav">
                <button type="button" class="icon-btn back-btn" @click="$router.go(-1)">
                    <van-icon name="arrow-left" />
                </button>
                <h1 class="nav-title">{{ getLabel('invest.wallet', 'Billetera') }}</h1>
                <button type="button" class="icon-btn refresh-btn" :aria-label="getLabel('pageInvest.refresh', 'Actualizar')" @click="onRefresh">
                    <van-icon name="replay" />
                </button>
            </header>

            <!-- 充值/提现 切换 -->
            <div class="wallet-tabs">
                <button type="button" class="tab" :class="{ active: tabActive === 0 }" @click="switchTab(0)">
                    {{ getLabel('invest.recharge', 'Recargar') }}
                </button>
                <button type="button" class="tab" :class="{ active: tabActive === 1 }" @click="switchTab(1)">
                    {{ getLabel('invest.withdraw', 'Retirar') }}
                </button>
            </div>

            <!-- ==================== 充值 Tab ==================== -->
            <template v-if="tabActive === 0">
            <!-- 支付方式 -->
            <section class="section">
                <h2 class="section-title">{{ getLabel('pageInvest.selectNetwork', 'Método de pago') }}</h2>
                <div class="pay-list">
                    <button
                        v-for="item in paymentList"
                        :key="item.id"
                        type="button"
                        class="pay-item"
                        :class="{ active: selectedPayment.id === item.id }"
                        @click="selectPayment(item)"
                    >
                        <span class="radio" aria-hidden="true"></span>
                        <span class="pay-icon">
                            <img v-if="item.logo" :src="item.logo" alt="">
                            <van-icon v-else name="bank-card-o" />
                        </span>
                        <span class="pay-info">
                            <b>{{ item.name }}</b>
                            <small>{{ item.mark || 'MXN' }}</small>
                        </span>
                        <van-icon v-if="selectedPayment.id === item.id" class="pay-check" name="success" />
                    </button>
                    <div v-if="!paymentList.length" class="pay-empty">
                        {{ getLabel('pageInvest.noPayment', 'Cargando métodos de pago...') }}
                    </div>
                </div>
            </section>

            <!-- 充值金额 -->
            <section class="amount-card">
                <input
                    v-model.trim="amount"
                    type="text"
                    inputmode="decimal"
                    class="amount-input"
                    :placeholder="getLabel('pageInvest.amountPlaceholder', 'MXN Ingrese el monto de recarga')"
                    @input="handleAmountInput"
                    @blur="refreshWalletInfo"
                >
            </section>

            <!-- 充值提示 -->
            <section class="tips-card">
                
                <h3 class="tips-title">{{ getLabel('pageInvest.tipsTitle', 'Consejos de Recarga') }}</h3>
                <ul class="tips-list">
                    <li>
                        {{ getLabel('pageInvest.tipMin', 'El monto mínimo de recarga es') }}
                        <b>{{ formatCurrency(config.min_recharge || 100) }}</b>
                    </li>
                    <li>
                        {{ getLabel('pageInvest.tipMax', 'El monto máximo de recarga es') }}
                        <b>{{ formatCurrency(config.max_recharge || 50000) }}</b>
                    </li>
                    <li>{{ getLabel('pageInvest.tipNoFee', 'Sin comisión') }}</li>
                    <li>{{ getLabel('pageInvest.tipTime', 'Tiempo de procesamiento: Crédito instantáneo') }}</li>
                </ul>
            </section>

            <!-- 确认按钮 -->
            <button
                type="button"
                class="confirm-btn"
                :disabled="rechargeLoading"
                @click="handleConfirmRecharge"
            >
     
                <span class="btn-text">{{ rechargeLoading ? getLabel('utils.loading', 'Procesando...') : getLabel('pageInvest.confirmRecharge', 'Confirmar Recarga') }}</span>
            </button>
            </template>

            <!-- ==================== 提现 Tab ==================== -->
            <template v-else>
                <section class="section">
                    <h2 class="section-title">{{ getLabel('pageCash.selectBankLabel', 'Seleccionar tarjeta bancaria') }}</h2>
                    <div class="bank-selector">
                        <button type="button" class="bank-bar" @click="bankDropdownOpen = !bankDropdownOpen">
                            <span :class="{ placeholder: !selectedBank.bank }">
                                {{ selectedBank.bank || getLabel('pageCash.selectBank', 'Por favor seleccione una tarjeta bancaria') }}
                            </span>
                            <van-icon :name="bankDropdownOpen ? 'arrow-up' : 'arrow-down'" />
                        </button>
                        <div v-if="bankDropdownOpen" class="bank-list">
                            <button
                                v-for="b in bankList"
                                :key="b.id"
                                type="button"
                                class="bank-item"
                                @click="selectBank(b)"
                            >
                                <img v-if="b.logo" :src="b.logo" alt="">
                                <div class="bank-meta">
                                    <b>{{ b.bank }}</b>
                                    <small>{{ b.account || b.bank }}</small>
                                </div>
                            </button>
                            <button type="button" class="bank-item add" @click="$router.push('/account')">
                                <van-icon name="plus" />
                                <span>{{ $t('account.add') }}</span>
                            </button>
                        </div>
                    </div>
                </section>

                <div class="available-row">
                    <span>{{ getLabel('pageCash.available', 'Monto disponible') }}</span>
                    <b>{{ formatCurrency(withdrawConfig.money || balance) }}</b>
                </div>

                <section class="section">
                    <h2 class="section-title">{{ getLabel('pageCash.amountLabel', 'Seleccionar monto de retiro') }}</h2>
                    <div class="wd-input-card">
                        <input
                            v-model.trim="withdrawMoney"
                            type="text"
                            inputmode="decimal"
                            class="wd-input"
                        >
                    </div>
                    <p class="wd-hint">
                        {{ getLabel('pageCash.amountHint', 'Debe ser un número entre') }}
                        {{ formatCurrency(minWithdrawAmount) }} y {{ formatCurrency(maxWithdrawAmount) }}
                    </p>
                    <p class="wd-max-note">
                        {{ getLabel('pageCash.maxNote', 'El monto máximo de retiro es') }} {{ formatCurrency(maxWithdrawAmount) }}
                    </p>
                    <div class="selected-row">
                        <span>{{ getLabel('pageCash.selected', 'Monto seleccionado:') }}</span>
                        <b>{{ formatCurrency(withdrawMoney) }}</b>
                    </div>
                    <div class="wd-pwd-card">
                        <van-icon name="lock" class="pwd-icon" />
                        <input
                            v-model.trim="withdrawPassword"
                            :type="passwordVisible ? 'text' : 'password'"
                            class="wd-pwd"
                            :placeholder="$t('cash.tips')"
                        >
                        <button type="button" class="eye-btn" @click="passwordVisible = !passwordVisible">
                            <van-icon :name="passwordVisible ? 'eye-o' : 'closed-eye'" />
                        </button>
                    </div>
                </section>

                <button type="button" class="confirm-btn dark" :disabled="withdrawLoading" @click="submitWithdraw">
                   
                    <span class="btn-text">{{ withdrawLoading ? getLabel('utils.loading', 'Procesando...') : getLabel('pageCash.confirmWithdraw', 'Confirmar Retiro') }}</span>
                </button>

                <section class="fee-card">
                   
                    <h3 class="fee-title">{{ getLabel('pageCash.feeTitle', 'Cálculo de comisión') }}</h3>
                    <div class="fee-row">
                        <span>{{ getLabel('pageCash.withdrawAmount', 'Monto de retiro') }}</span>
                        <b>{{ formatCurrency(withdrawMoney) }}</b>
                    </div>
                    <div class="fee-row">
                        <span>{{ getLabel('pageCash.feeRate', 'Tasa de comisión') }}</span>
                        <b>{{ withdrawRatePercent.toFixed(2) }}%</b>
                    </div>
                    <div class="fee-row">
                        <span>{{ getLabel('pageCash.feeAmount', 'Comisión de retiro') }}</span>
                        <b>{{ formatCurrency(withdrawFee) }}</b>
                    </div>
                    <div class="fee-row final">
                        <span>{{ getLabel('pageCash.finalAmount', 'Monto final') }}</span>
                        <b>{{ formatCurrency(withdrawFinal) }}</b>
                    </div>
                </section>

                <section class="instructions-card">
                    <h3 class="instructions-title">{{ getLabel('pageCash.instructionsTitle', 'Instrucciones para Retiros') }}</h3>
                    <ol class="instructions-list">
                        <li>{{ getLabel('pageCash.inst1', 'Los retiros se aceptan diariamente de 9:00 a. m. a 18:00 p. m.') }}</li>
                        <li>{{ getLabel('pageCash.inst2', 'Solo se puede enviar una solicitud de retiro por día calendario.') }}</li>
                        <li>{{ getLabel('pageCash.inst3', 'Los sábados y domingos no son días hábiles bancarios; no se aceptarán solicitudes de retiro en estos días.') }}</li>
                        <li>{{ getLabel('pageCash.inst4', 'Se deducirá una comisión de gestión del 10 % y los impuestos correspondientes de cada solicitud de retiro. La comisión se descuenta directamente del monto retirado.') }}</li>
                        <li>{{ getLabel('pageCash.inst5', 'Los nuevos usuarios deben completar la verificación de identidad para su primer retiro. Después de una verificación exitosa, los retiros posteriores no requerirán verificación adicional.') }}</li>
                        <li>{{ getLabel('pageCash.inst6', 'Una vez aprobada su solicitud de retiro, los fondos llegarán en un plazo de 0 a 48 horas. El tiempo exacto de llegada depende del sistema del banco.') }}</li>
                    </ol>
                </section>
            </template>
        </main>

        <!-- 底部宝箱装饰 -->
       

        <!-- 底部导航 -->
        <nav class="bottom-nav">
            <router-link to="/index" class="nav-item">
                <van-icon name="home-o" />
                <span>{{ $t('home.index') }}</span>
            </router-link>
            <router-link to="/activity" class="nav-item">
                <van-icon name="star-o" />
                <span>{{ $t('home.activity') }}</span>
            </router-link>
            <router-link to="/myTeam" class="nav-item">
                <van-icon name="friends-o" />
                <span>{{ $t('new_lang.team') }}</span>
            </router-link>
            <router-link to="/invest" class="nav-item active">
                <van-icon name="balance-o" />
                <span>{{ getLabel('invest.recharge', 'Recargar') }}</span>
            </router-link>
            <router-link to="/user" class="nav-item">
                <van-icon name="contact" />
                <span>{{ $t('home.my') }}</span>
            </router-link>
        </nav>

        <!-- 成功弹窗 -->
        <div v-if="showSuccessPopup" class="success-popup-mask" @click.self="closeSuccessPopup">
            <div class="success-popup">
                <div class="success-icon">
                    <van-icon name="passed" />
                </div>
                <h3>{{ getLabel('pageInvest.successTitle', 'Solicitud enviada') }}</h3>
                <p>{{ getLabel('pageInvest.successText', 'Tu recarga está siendo procesada.') }}</p>
                <button type="button" class="success-btn" @click="goPay">{{ getLabel('pageInvest.goPay', 'Ir a pagar') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import Fetch from "../../utils/fetch";
import { Icon, Toast } from "vant";

Vue.use(Icon);
Vue.use(Toast);

export default {
    name: "invest",
    data() {
        return {
            tabActive: 0,
            lang: this.$i18n.locale || "zh_cn",
            balance: 0,
            config: {},
            paymentList: [],
            selectedPayment: {},
            walletData: {},
            recordList: [],
            dropdownOpen: false,
            amount: "",
            submitLoading: false,
            showSuccessPopup: false,
            amountTimer: null,
            appOriginalStyles: null,
            bodyOriginalBackground: "",
            rechargeLoading: false,
            payUrl: '',

            // 提现
            bankList: [],
            selectedBank: {},
            bankDropdownOpen: false,
            withdrawConfig: {},
            withdrawMoney: "",
            withdrawPassword: "",
            passwordVisible: false,
            withdrawCharge: 0,
            withdrawType: 1,
            minWithdrawAmount: 100,
            maxWithdrawAmount: 50000,
            withdrawLoading: false,
            setPwd: false,
        };
    },
    computed: {
        selectedPaymentLabel() {
            if (!this.selectedPayment.name) {
                return this.getLabel("pageInvest.selectNetwork", "Seleccionar método de pago");
            }
            return this.selectedPayment.name;
        },
        defaultMoney() {
            const typedMoney = Number(this.amount || 0);
            if (typedMoney > 0) {
                return typedMoney;
            }
            const queryMoney = Number(this.$route.query.money || 0);
            if (queryMoney > 0) {
                return queryMoney;
            }
            return Number(this.config.min_recharge || 0);
        },
        withdrawFee() {
            const money = Number(this.withdrawMoney || 0);
            if (!money) return 0;
            if (Number(this.withdrawType) === 1) {
                return Number(this.withdrawCharge || 0);
            }
            return money * Number(this.withdrawCharge || 0) / 100;
        },
        withdrawRatePercent() {
            const money = Number(this.withdrawMoney || 0);
            if (Number(this.withdrawType) === 1) {
                return money > 0 ? (Number(this.withdrawCharge || 0) / money * 100) : 0;
            }
            return Number(this.withdrawCharge || 0);
        },
        withdrawFinal() {
            return Math.max(Number(this.withdrawMoney || 0) - this.withdrawFee, 0);
        },
    },
    watch: {
        // 从路由动态获取：invest?tabActive=1，路由变化时同步更新 tabActive
        "$route.query.tabActive": {
            immediate: true,
            handler(val) {
                this.tabActive = this.parseTab(val);
            },
        },
    },
    created() {
        this.$parent.footer("user", false);
    },
    mounted() {
        this.applyFullScreenLayout();
        this.start();
        document.addEventListener("click", this.handleDocumentClick);
    },
    beforeDestroy() {
        if (this.amountTimer) {
            clearTimeout(this.amountTimer);
        }
        document.removeEventListener("click", this.handleDocumentClick);
        this.restoreFullScreenLayout();
    },
    methods: {
        getLabel(key, defaultText) {
            return this.$te(key) ? this.$t(key) : defaultText;
        },
        onRefresh() {
            this.start();
        },
        parseTab(val) {
            if (Array.isArray(val)) {
                val = val[0];
            }
            const num = Number(val);
            return num === 0 || num === 1 ? num : 0;
        },
        switchTab(idx) {
            this.tabActive = this.parseTab(idx);
            this.bankDropdownOpen = false;
            // 将当前 tab 同步写回路由，保证 URL 与界面一致
            const next = String(this.tabActive);
            if (this.$route.query.tabActive !== next) {
                this.$router.replace({
                    query: { ...this.$route.query, tabActive: next },
                });
            }
        },
        /* ---------- 提现 ---------- */
        selectBank(item) {
            this.selectedBank = item;
            this.bankDropdownOpen = false;
            this.withdrawCharge = Number(item.charge || 0);
            this.withdrawType = Number(item.type || 1);
        },
        async submitWithdraw() {
            if (this.withdrawLoading) return;
            if (!this.selectedBank || !this.selectedBank.id) {
                Toast(this.getLabel('pageCash.selectBankFirst', 'Por favor selecciona una tarjeta bancaria'));
                return;
            }
            const money = Number(this.withdrawMoney || 0);
            if (!money || money <= 0) {
                Toast(this.getLabel('pageCash.enterAmount', 'Por favor ingresa el monto de retiro'));
                return;
            }
            if (money < this.minWithdrawAmount) {
                Toast(this.getLabel('pageCash.minError', 'Monto mínimo de retiro') + ' ' + this.formatCurrency(this.minWithdrawAmount));
                return;
            }
            if (money > Number(this.withdrawConfig.money || this.balance || 0)) {
                Toast(this.$t('cash.withdrawalAmountHigh'));
                return;
            }
            if (!this.withdrawPassword) {
                Toast(this.$t('cash.tips'));
                return;
            }
            this.withdrawLoading = true;
            try {
                await Fetch("/user/cost_apply", {
                    money: money,
                    bank_id: this.selectedBank.id,
                    passwd: this.withdrawPassword,
                });
                this.withdrawPassword = "";
                this.withdrawMoney = "";
                this.showSuccessPopup = true;
                await this.loadWithdraw();
            } catch (e) {
                console.error('withdraw error:', e);
            } finally {
                this.withdrawLoading = false;
            }
        },
        async loadWithdraw() {
            try {
                const walletRes = await Fetch("/user/my_bank");
                const data = walletRes.data || {};
                this.withdrawConfig = data;
                this.setPwd = !!data.intPwd;
                this.minWithdrawAmount = Number(data.min_withdraw || data.min_cash || data.minmoney || 100);
                this.maxWithdrawAmount = Number(data.max_withdraw || data.max_cash || 50000);
                const bankList = Array.isArray(data.bank) ? data.bank : [];
                this.bankList = bankList.map((item) => ({
                    ...item,
                    id: item.id,
                    bank: item.bank,
                    logo: item.logo || "",
                    account: item.account || "",
                    charge: item.charge || 0,
                    type: item.type || 1,
                }));
                if (this.bankList.length && !this.selectedBank.id) {
                    this.selectBank(this.bankList[0]);
                }
            } catch (e) {
                this.bankList = [];
            }
        },
        handleDocumentClick(event) {
            if (!this.$el || !this.bankDropdownOpen) return;
            const sel = this.$el.querySelector(".bank-selector");
            if (sel && !sel.contains(event.target)) this.bankDropdownOpen = false;
        },
        async handleConfirmRecharge() {
            if (this.rechargeLoading) return;

            if (!this.selectedPayment || !this.selectedPayment.id) {
                this.$toast(this.getLabel('pageInvest.pleaseSelectNetwork', 'Por favor selecciona un método de pago'));
                return;
            }

            const amountNum = Number(this.amount);
            if (!this.amount || isNaN(amountNum) || amountNum <= 0) {
                this.$toast(this.getLabel('pageInvest.pleaseEnterAmount', 'Por favor ingresa un monto válido'));
                return;
            }

            const minRecharge = Number(this.config.min_recharge || 0);
            if (minRecharge > 0 && amountNum < minRecharge) {
                this.$toast(
                    this.getLabel('pageInvest.minRecharge', 'Monto mínimo') + ' ' + this.formatCurrency(minRecharge)
                );
                return;
            }

            this.rechargeLoading = true;
            try {
                const res = await Fetch("/user/recharge_apply", {
                    payment_id: this.selectedPayment.id,
                    money: amountNum,
                });
                this.showSuccessPopup = true;
                await this.loadRecords();

                if (res && res.code === 1) {
                    this.$toast.success(this.getLabel('pageInvest.rechargeSuccess', 'Solicitud enviada'));
                    this.payUrl = res.data.payUrl;
                    this.goPay();
                    if (typeof this.refreshWalletInfo === 'function') {
                        await this.refreshWalletInfo();
                    }
                } else {
                    this.$toast(res?.msg || this.getLabel('pageInvest.rechargeFailed', 'Falló la recarga, por favor inténtalo de nuevo'));
                }
            } catch (err) {
                console.error('handleConfirmRecharge error:', err);
                this.$toast(this.getLabel('pageInvest.rechargeFailed', 'Falló la recarga, por favor inténtalo de nuevo'));
            } finally {
                this.rechargeLoading = false;
            }
        },
        goPay() {
            if (!this.payUrl) {
                this.showSuccessPopup = false;
                return;
            }
            window.location.href = this.payUrl;
            this.showSuccessPopup = false;
        },
        applyFullScreenLayout() {
            const app = document.getElementById("app");
            if (!app) {
                return;
            }
            this.appOriginalStyles = {
                maxWidth: app.style.maxWidth,
                width: app.style.width,
                margin: app.style.margin,
                backgroundColor: app.style.backgroundColor,
                paddingBottom: app.style.paddingBottom,
            };
            this.bodyOriginalBackground = document.body.style.background;
            app.style.maxWidth = "100%";
            app.style.width = "100%";
            app.style.margin = "0";
            app.style.backgroundColor = "#05070f";
            app.style.paddingBottom = "0";
            document.body.style.background = "#05070f";
        },
        restoreFullScreenLayout() {
            const app = document.getElementById("app");
            if (app && this.appOriginalStyles) {
                app.style.maxWidth = this.appOriginalStyles.maxWidth || "";
                app.style.width = this.appOriginalStyles.width || "";
                app.style.margin = this.appOriginalStyles.margin || "";
                app.style.backgroundColor = this.appOriginalStyles.backgroundColor || "";
                app.style.paddingBottom = this.appOriginalStyles.paddingBottom || "";
            }
            document.body.style.background = this.bodyOriginalBackground || "";
        },
        async start() {
            const rechargeRes = await Fetch("/user/recharge", {});
            this.balance = rechargeRes.data.money || 0;
            this.config = rechargeRes.data || {};
            this.paymentList = Array.isArray(rechargeRes.data.payment) ? rechargeRes.data.payment : [];
            const queryMoney = Number(this.$route.query.money || 0);
            if (queryMoney > 0) {
                this.amount = String(queryMoney);
            }

            await this.loadRecords();

            if (this.paymentList.length) {
                this.selectPayment(this.paymentList[0]);
            }

            await this.loadWithdraw();
        },
        async loadRecords() {
            try {
                const recordRes = await Fetch("/user/funds", { reason_id: 1 });
                this.recordList = Array.isArray(recordRes.data.list) ? recordRes.data.list : [];
            } catch (e) {
                this.recordList = [];
            }
        },
        async selectPayment(item) {
            this.selectedPayment = item;
            await this.refreshWalletInfo();
        },
        async refreshWalletInfo() {
            if (!this.selectedPayment || !this.selectedPayment.id) {
                this.walletData = {};
                return;
            }
            try {
                const detailRes = await Fetch("/user/recharge_type", {
                    money: this.defaultMoney,
                    id: this.selectedPayment.id,
                });
                this.walletData = detailRes.data || {};
            } catch (error) {
                this.walletData = {};
            }
        },
        handleAmountInput() {
            if (this.amountTimer) {
                clearTimeout(this.amountTimer);
            }
            this.amountTimer = setTimeout(() => {
                this.refreshWalletInfo();
            }, 350);
        },
        formatCurrency(value) {
            return "MXN" + Number(value || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        closeSuccessPopup() {
            this.showSuccessPopup = false;
        },
    },
};
</script>

<style lang="less" scoped>
.invest-page {
    min-height: 100vh;
    background: #05070f;
    color: #fff;
}

.page-shell {
    width: 100%;
    max-width: 430px;
    min-height: 100vh;
    margin: 0 auto;
    padding: 0 14px 120px;
    box-sizing: border-box;
    background:
        radial-gradient(circle at 80% 0%, rgba(30, 120, 200, 0.12), transparent 45%),
        #05070f;
}

/* ---------- 顶部栏 ---------- */
.top-nav {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    padding: 0 14px;
    margin-bottom: 16px;
}

.icon-btn {
    width: 34px;
    height: 34px;
    padding: 0;
    border: 1px solid rgba(90, 130, 200, 0.3);
    border-radius: 50%;
    background: rgba(20, 35, 65, 0.6);
    color: #cdd8ee;
    display: grid;
    place-items: center;
    cursor: pointer;
}

.icon-btn .van-icon {
    font-size: 18px;
}

.back-btn {
    position: absolute;
    left: 0;
}

.refresh-btn {
    position: absolute;
    right: 0;
}

.nav-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #eef3ff;
}

/* ---------- Tab 切换 ---------- */
.wallet-tabs {
    display: flex;
    gap: 4px;
    padding: 4px;
    margin-bottom: 20px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 14px;
    background: #0a1424;
}

.tab {
    flex: 1;
    height: 42px;
    padding: 0;
    border: 0;
    border-radius: 11px;
    background: transparent;
    color: #8a9bb8;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
}

.tab.active {
    background: linear-gradient(135deg, #2fd0e6, #27b6d6);
    color: #04222b;
    box-shadow: 0 4px 14px rgba(45, 190, 220, 0.35);
}

/* ---------- 区块 ---------- */
.section {
    margin-bottom: 18px;
}

.section-title {
    margin: 0 0 12px;
    color: #c5cfe4;
    font-size: 14px;
    font-weight: 500;
}

/* ---------- 支付方式列表 ---------- */
.pay-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.pay-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 14px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 12px;
    background: rgba(12, 24, 46, 0.72);
    text-align: left;
    cursor: pointer;
    box-sizing: border-box;
}

.pay-item.active {
    border-color: rgba(45, 190, 220, 0.7);
    background: rgba(16, 40, 60, 0.85);
    box-shadow: 0 0 0 1px rgba(45, 190, 220, 0.35);
}

.radio {
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
    border: 1.5px solid #5a6f9e;
    border-radius: 50%;
    position: relative;
}

.pay-item.active .radio {
    border-color: #2fd0e6;
}

.pay-item.active .radio::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 9px;
    height: 9px;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: #2fd0e6;
}

.pay-icon {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #1a2742;
    color: #7d92b8;
    display: grid;
    place-items: center;
    overflow: hidden;
}

.pay-item.active .pay-icon {
    background: rgba(45, 208, 230, 0.18);
    color: #2fd0e6;
}

.pay-icon img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
}

.pay-icon .van-icon {
    font-size: 20px;
}

.pay-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.pay-info b {
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pay-item.active .pay-info b {
    color: #2fd0e6;
}

.pay-info small {
    color: #8a9bb8;
    font-size: 11px;
}

.pay-check {
    flex: 0 0 auto;
    color: #2fd0e6;
    font-size: 18px;
}

.pay-empty {
    padding: 20px;
    border: 1px dashed rgba(90, 130, 200, 0.3);
    border-radius: 12px;
    color: #6f7f9e;
    font-size: 13px;
    text-align: center;
}

/* ---------- 金额输入 ---------- */
.amount-card {
    margin-bottom: 14px;
    padding: 0 14px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-bottom: 2px solid #2fd0e6;
    border-radius: 12px;
    background: rgba(10, 20, 40, 0.85);
}

.amount-input {
    width: 100%;
    height: 56px;
    padding: 0;
    border: 0;
    outline: none;
    background: transparent;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    box-sizing: border-box;
}

.amount-input::placeholder {
    color: #5a6f9e;
    font-size: 14px;
    font-weight: 400;
}

/* ---------- 提示卡 ---------- */
.tips-card {
    position: relative;
    margin-bottom: 18px;
    padding: 14px 14px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 12px;
    background: rgba(10, 20, 40, 0.7);
    overflow: hidden;
}

.tips-avatar {
    position: absolute;
    right: 12px;
    top: 50%;
    width: 44px;
    height: 44px;
    transform: translateY(-50%);
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.2);
    background: radial-gradient(circle at 35% 30%, #ffd0a0, #c45a8a 50%, #4a1a4a);
}

.tips-title {
    margin: 0 0 10px;
    padding-right: 54px;
    color: #e6edfb;
    font-size: 14px;
    font-weight: 700;
}

.tips-list {
    margin: 0;
    padding: 0;
    list-style: none;
    padding-right: 54px;
}

.tips-list li {
    position: relative;
    padding-left: 14px;
    margin-bottom: 6px;
    color: #9fb0cc;
    font-size: 12px;
    line-height: 1.5;
}

.tips-list li::before {
    content: "•";
    position: absolute;
    left: 2px;
    color: #5a6f9e;
}

.tips-list b {
    color: #dce6f5;
    font-weight: 600;
}

/* ---------- 确认按钮 ---------- */
.confirm-btn {
    position: relative;
    width: 100%;
    height: 54px;
    border: 0;
    border-radius: 12px;
    background: linear-gradient(90deg, #2fd0e6, #27b6d6);
    box-shadow: 0 6px 20px rgba(34, 15, 102, 0.86);
    color: #04222b;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 1px;
    cursor: pointer;
    overflow: hidden;
    margin-bottom: 15px;
}

.confirm-btn[disabled] {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-text {
    position: relative;
    z-index: 1;
}

.btn-wheel {
    position: absolute;
    right: 10px;
    top: 50%;
    width: 36px;
    height: 36px;
    transform: translateY(-50%);
    border-radius: 50%;
    background: conic-gradient(
        #ffd25e 0 30deg, #ff6b6b 30deg 60deg,
        #4ecdc4 60deg 90deg, #6c5ce7 90deg 120deg,
        #ffd25e 120deg 150deg, #ff6b6b 150deg 180deg,
        #4ecdc4 180deg 210deg, #6c5ce7 210deg 240deg,
        #ffd25e 240deg 270deg, #ff6b6b 270deg 300deg,
        #4ecdc4 300deg 330deg, #6c5ce7 330deg 360deg
    );
    box-shadow: 0 0 10px rgba(108, 92, 231, 0.5);
}

.btn-wheel::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 7px;
    height: 7px;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: #fff;
}

/* ---------- 漂浮宝箱 ---------- */
.float-chest {
    position: fixed;
    right: max(20px, calc((100vw - 430px) / 2 + 20px));
    bottom: max(120px, calc(env(safe-area-inset-bottom) + 120px));
    z-index: 40;
    width: 56px;
    height: 48px;
    border-radius: 10px;
    background: linear-gradient(180deg, #ffcf5a, #b8860b);
    box-shadow: 0 0 16px rgba(240, 181, 58, 0.5);
    pointer-events: none;
}

.float-chest::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 8px;
    transform: translateY(-50%);
    background: rgba(120, 70, 10, 0.7);
}

/* ---------- 底部导航 ---------- */
.bottom-nav {
    position: fixed;
    left: 50%;
    bottom: 0;
    z-index: 50;
    width: 100%;
    max-width: 430px;
    padding: 8px 6px max(8px, env(safe-area-inset-bottom));
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.92);
    backdrop-filter: blur(16px);
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: space-around;
    box-sizing: border-box;
}

.nav-item {
    flex: 1;
    min-width: 0;
    padding: 6px 4px;
    color: #6b7280;
    text-decoration: none;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.nav-item .van-icon {
    font-size: 22px;
}

.nav-item span {
    margin-top: 4px;
    font-size: 11px;
    line-height: 1.25;
    font-weight: 500;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.nav-item.active,
.nav-item.router-link-exact-active {
    color: #2fd0e6;
}

.nav-item.active span,
.nav-item.router-link-exact-active span {
    font-weight: 700;
}

/* ---------- 成功弹窗 ---------- */
.success-popup-mask {
    position: fixed;
    inset: 0;
    z-index: 120;
    background: rgba(0, 0, 0, 0.68);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.success-popup {
    width: 100%;
    max-width: 320px;
    padding: 28px 22px 22px;
    border: 1px solid rgba(45, 190, 220, 0.25);
    border-radius: 18px;
    background: linear-gradient(180deg, #081122 0%, #050a16 100%);
    box-shadow: 0 18px 45px rgba(0, 0, 0, 0.38);
    text-align: center;
}

.success-icon {
    width: 66px;
    height: 66px;
    margin: 0 auto 16px;
    border-radius: 50%;
    background: rgba(45, 208, 230, 0.14);
    color: #2fd0e6;
    display: flex;
    align-items: center;
    justify-content: center;
}

.success-icon .van-icon {
    font-size: 34px;
}

.success-popup h3 {
    margin: 0 0 10px;
    color: #fff;
    font-size: 20px;
    font-weight: 700;
}

.success-popup p {
    margin: 0 0 20px;
    color: rgba(255, 255, 255, 0.64);
    font-size: 13px;
    line-height: 1.7;
}

.success-btn {
    width: 100%;
    height: 46px;
    border: 0;
    border-radius: 12px;
    background: linear-gradient(135deg, #2fd0e6, #27b6d6);
    color: #04222b;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
}

/* ---------- 提现：银行卡选择 ---------- */
.bank-selector { position: relative; }

.bank-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 50px;
    padding: 0 14px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 12px;
    background: rgba(12, 24, 46, 0.72);
    color: #fff;
    font-size: 14px;
    cursor: pointer;
    box-sizing: border-box;
}

.bank-bar span.placeholder { color: #6f7f9e; }
.bank-bar .van-icon { color: #8a9bb8; font-size: 16px; }

.bank-list {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 6px);
    z-index: 30;
    overflow: hidden;
    border: 1px solid rgba(70, 110, 190, 0.4);
    border-radius: 12px;
    background: rgba(15, 28, 52, 0.98);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
}

.bank-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 12px 14px;
    border: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    background: transparent;
    color: #fff;
    text-align: left;
    cursor: pointer;
}

.bank-item:last-child { border-bottom: 0; }

.bank-item img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
}

.bank-meta { min-width: 0; }
.bank-meta b { display: block; font-size: 14px; }
.bank-meta small { color: #8a9bb8; font-size: 11px; }

.bank-item.add {
    color: #2fd0e6;
    font-size: 13px;
    justify-content: center;
}

/* ---------- 提现：可用余额行 ---------- */
.available-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
    padding: 14px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 12px;
    background: rgba(12, 24, 46, 0.5);
}

.available-row span { color: #9fb0cc; font-size: 13px; }
.available-row b { color: #fff; font-size: 16px; font-weight: 700; }

/* ---------- 提现：金额输入 ---------- */
.wd-input-card {
    padding: 0 14px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 12px;
    background: rgba(10, 20, 40, 0.85);
}

.wd-input {
    width: 100%;
    height: 54px;
    padding: 0;
    border: 0;
    outline: none;
    background: transparent;
    color: #fff;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 1px;
    box-sizing: border-box;
}

.wd-hint {
    margin: 10px 2px 4px;
    color: #6f7f9e;
    font-size: 11px;
}

.wd-max-note {
    margin: 0 2px 12px;
    color: #ff6b8a;
    font-size: 11px;
}

.selected-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    margin-bottom: 12px;
}

.selected-row span { color: #9fb0cc; font-size: 13px; }
.selected-row b { color: #2fd0e6; font-size: 16px; font-weight: 700; }

.wd-pwd-card {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 50px;
    padding: 0 14px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 12px;
    background: rgba(10, 20, 40, 0.85);
}

.pwd-icon { color: #8a9bb8; font-size: 18px; }

.wd-pwd {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    color: #fff;
    font-size: 15px;
}

.eye-btn {
    border: 0;
    background: transparent;
    color: #8a9bb8;
    cursor: pointer;
}

.eye-btn .van-icon { font-size: 20px; }

/* ---------- 提现：深色确认按钮 ---------- */
.confirm-btn.dark {
    background: #0d1830;
    border: 1px solid rgba(70, 110, 190, 0.35);
    color: #9fb0cc;
    box-shadow: none;
}

/* ---------- 提现：费用卡 ---------- */
.fee-card {
    position: relative;
    margin-bottom: 18px;
    padding: 14px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 12px;
    background: rgba(10, 20, 40, 0.7);
    overflow: hidden;
}

.fee-chest {
    position: absolute;
    right: 12px;
    top: 14px;
    width: 44px;
    height: 38px;
    border-radius: 8px;
    background: linear-gradient(180deg, #ffcf5a, #b8860b);
    box-shadow: 0 0 12px rgba(240, 181, 58, 0.5);
}

.fee-chest::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 6px;
    transform: translateY(-50%);
    background: rgba(120, 70, 10, 0.7);
}

.fee-title {
    margin: 0 0 12px;
    padding-right: 54px;
    color: #2fd0e6;
    font-size: 14px;
    font-weight: 700;
}

.fee-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 0;
    font-size: 12px;
}

.fee-row span { color: #9fb0cc; }
.fee-row b { color: #fff; font-weight: 600; }
.fee-row.final span { color: #2fd0e6; }
.fee-row.final b { color: #2fd0e6; font-size: 14px; }

/* ---------- 提现：说明卡 ---------- */
.instructions-card {
    padding: 14px;
    border: 1px solid rgba(70, 110, 190, 0.3);
    border-radius: 12px;
    background: rgba(10, 20, 40, 0.5);
}

.instructions-title {
    margin: 0 0 10px;
    color: #2fd0e6;
    font-size: 14px;
    font-weight: 700;
}

.instructions-list {
    margin: 0;
    padding-left: 18px;
}

.instructions-list li {
    margin-bottom: 6px;
    color: #9fb0cc;
    font-size: 11.5px;
    line-height: 1.6;
}

@media (max-width: 360px) {
    .page-shell {
        padding-left: 10px;
        padding-right: 10px;
    }

    .tips-list {
        padding-right: 46px;
    }

    .tips-avatar {
        width: 38px;
        height: 38px;
    }
}
</style>
